/* eslint-disable react-hooks/exhaustive-deps */
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  endAt,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  startAt,
  updateDoc,
  where,
  writeBatch
} from "firebase/firestore";
import { lockerSetup, shard } from "../utils/constants";
import { isValidArray, isValidObject } from "../utils/validators";
import { getCurrentUser } from "./authentication";
import { throwError } from "./error";
import { database } from "./firebase";

export const customAddDoc = async (ref, data) => {
  let doc;
  try {
    doc = await addDoc(ref, data);
  } catch (error) {
    if (error.message.includes("Document already exists")) {
      doc = {};
      doc.id = error.message.split("/")[error.message.split("/").length - 1];
      console.warn("Document creation ID collision at create profile");
    } else if (error.message.includes("entity already exists")) {
      if (
        typeof error.message.split(`"`)[5] === "string" &&
        error.message.split(`"`)[5].length > 10
      ) {
        doc = {};
        doc.id = error.message.split(`"`)[5];
      }
    } else {
      throw error;
    }
  }
  return doc.id;
};

//To create support query
export const createQuery = async (data) => {
  await customAddDoc(collection(database, "queries"), data);
};

//To check whether the query exists in database
export const checkQueryExists = async (docId, phoneNumber) => {
  const queriesRef = query(
    collection(database, "queries"),
    where("document.id", "==", docId),
    where("phoneNumber", "==", phoneNumber)
  );
  const queriesSnapshot = await getDocs(queriesRef);
  let queries = [];
  queriesSnapshot.forEach((query) => {
    queries.push(query.data());
  });
  if (queries.length === 0) {
    return false;
  } else return true;
};

//To delete the patient profile
export const deleteProfile = async (profileId, phoneNumber) => {
  const publicRef = doc(
    database,
    "patients",
    profileId,
    "patientsPublic",
    "profile"
  );
  try {
    await updateDoc(publicRef, {
      phoneNumber: ""
    });
  } catch (error) {
    console.warn(
      "public profile update failed, checking if profile is updated..."
    );
    let data = null;
    try {
      data = (await getDoc(publicRef)).data();
    } catch (error) {
      console.warn(
        "public profile unable to be read, profile is possibly updated..."
      );
    }
    if (data.phoneNumber !== "") {
      throw error;
    }
  }
  const privateRef = doc(
    database,
    "patients",
    profileId,
    "patientsPrivate",
    "profile"
  );
  try {
    await updateDoc(privateRef, {
      phoneNumber: ""
    });
  } catch (error) {
    console.warn(
      "private profile update failed, checking if profile is updated..."
    );
    let data = null;
    try {
      data = (await getDoc(privateRef)).data();
    } catch (error) {
      console.warn(
        "private profile unable to be read, profile is possibly updated..."
      );
    }
    if (data !== null) {
      throw error;
    }
  }
  const rootRef = doc(database, "patients", profileId);
  try {
    await updateDoc(rootRef, {
      archive: {
        phoneNumber: phoneNumber,
        deletionRequestedAt: +new Date()
      },
      phoneNumber: "",
      uid: ""
    });
  } catch (error) {
    console.warn(
      "root profile update failed, checking if profile is updated..."
    );
    let data = null;
    try {
      data = (await getDoc(rootRef)).data();
    } catch (error) {
      console.warn(
        "root profile unable to be read, profile is possibly updated..."
      );
    }
    if (data !== null) {
      throw error;
    }
  }
};

//To create symptom
export async function addSymptom(data) {
  const symptomData = {
    ...data,
    url: {
      audio: typeof data.url.audio === "string" ? data.url.audio : "",
      images: [
        ...(isValidObject(data.url) && isValidArray(data.url.images)
          ? data.url.images
          : [])
      ]
    },
    location: {
      front: [
        ...data.location.front.map((value) => ({
          posX: value.posX,
          posY: value.posY
        }))
      ],
      back: [
        ...data.location.back.map((value) => ({
          posX: value.posX,
          posY: value.posY
        }))
      ]
    },
    to: {
      id: data.to.id,
      fullName: data.to.fullName,
      phoneNumber: data.to.phoneNumber
    }
  };
  const docRef = collection(database, "documents");
  const documentRes = await customAddDoc(docRef, symptomData);
  return documentRes;
}

//To update symptom
export async function updateSymptom(documentId, data) {
  const symptomData = {
    ...data,
    location: {
      front: [...data.location.front],
      back: [...data.location.back]
    },
    to: {
      id: data.to.id,
      phoneNumber: data.to.phoneNumber,
      fullName: data.to.fullName
    }
  };
  const docRef = doc(database, "documents", documentId);
  await updateDoc(docRef, symptomData);
  return documentId;
}

//create vitals
export const createVitals = async (data) => {
  const createVitalsResponse = await customAddDoc(
    collection(database, "documents"),
    data
  );
  return createVitalsResponse;
};

//To remove clinics connection
export async function deleteConnectedClinic(clinicId, currentProfile) {
  await deleteDoc(
    doc(database, "patients", currentProfile, "connections", clinicId)
  );
}

//patient profile picture update
export async function setProfilePicturePath(phoneNumber, profileId) {
  const privateRef = doc(
    database,
    "patients",
    profileId,
    "patientsPrivate",
    "profile"
  );
  await setDoc(
    privateRef,
    {
      profilePicture: `gs://ninto-profile-pictures/patients/${phoneNumber}/${profileId}.png`
    },
    { merge: true }
  );
}

export async function addDocumentsToDatabase(data) {
  const documentRef = doc(database, "documents");
  return await customAddDoc(documentRef, data);
}

//mark all as read database function
export async function setAllNotificationAsRead(notificationsList) {
  if (isValidArray(notificationsList)) {
    const batch = writeBatch(database);
    notificationsList?.forEach((notification) => {
      const documentRef = doc(
        database,
        "patients",
        notification?.profileId,
        "notifications",
        notification?.documentId
      );
      batch.update(documentRef, { read: true });
    });
    await batch.commit();
    return { success: true };
  } else {
    throwError("custom", "Something went wrong");
  }
}

export async function setNotificationRead(notification, profileId) {
  const notificationRef = doc(
    database,
    "patients",
    profileId,
    "notifications",
    notification
  );
  await updateDoc(notificationRef, {
    read: true
  });
  return { success: true };
}

export async function getPaginatedNotifications(
  profileId,
  lastVisible,
  pageNumber,
  update
) {
  const notifications = {};
  const validDate = new Date().setHours(0, 0, 0, 0);
  const paginationNotifications = query(
    collection(database, "patients", profileId, "notifications"),
    // where("shard", "in", shard.value),
    where("timestamp", "<=", +validDate + 86400000 * 2),
    orderBy("timestamp", "desc"),
    startAfter(lastVisible),
    limit(50)
  );

  const querySnapshot = await getDocs(paginationNotifications);
  querySnapshot.forEach((doc) => {
    if (isValidObject(doc.data())) {
      notifications[doc.id] = {
        documentId: doc.id,
        ...(update
          ? { pageNumber: pageNumber }
          : { pageNumber: pageNumber + 1 }),
        ...doc.data()
      };
    }
  });
  const lastVisibleDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
  return {
    notifications: notifications,
    lastVisible: isValidObject(lastVisibleDoc) ? lastVisibleDoc : "end"
  };
}

export async function editProfile(pid, data) {
  //data filter
  const patientPublic = {
    ...(data.fullName ? { fullName: data.fullName } : {})
  };
  const patientPrivate = {
    ...(data.bloodGroup ? { bloodGroup: data.bloodGroup } : {}),
    ...(data.dateOfBirth ? { dateOfBirth: data.dateOfBirth } : {}),
    ...(data.gender ? { gender: data.gender } : {}),
    ...(data.height ? { height: parseInt(data.height) } : {}),
    ...(data.weight ? { weight: parseInt(data.weight) } : {}),
    ...(data.allergies ? { allergies: [...data.allergies] } : {})
  };

  //set patients public profile
  if (Object.keys(patientPublic).length > 0) {
    const publicRef = doc(
      database,
      "patients",
      pid,
      "patientsPublic",
      "profile"
    );
    await updateDoc(publicRef, patientPublic);
  }
  //set patients public profile
  if (Object.keys(patientPrivate).length > 0) {
    const privateRef = doc(
      database,
      "patients",
      pid,
      "patientsPrivate",
      "profile"
    );
    await updateDoc(privateRef, patientPrivate);
  }
}

export const deleteConnectionRequest = async (inviteCode) => {
  await deleteDoc(doc(database, "requests", inviteCode));
};

//update form
export const updateForm = async (documentId, data) => {
  const formRef = doc(database, "documents", documentId);
  await updateDoc(formRef, data);
  return true;
};

export async function notificationPaginationQuery(
  profileId,
  phoneNumber,
  paginationNumber
) {
  const currentDate = new Date();
  const startingTimeStamp = +new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    (currentDate.getDate() - paginationNumber) * 7
  );
  const endingTimeStamp = +new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    (currentDate.getDate() - (paginationNumber + 1)) * 7
  );

  const collectionQueryRef = query(
    collectionGroup(database, "notifications"),
    where("phoneNumber", "==", phoneNumber),
    where("shard", "in", shard.value),
    orderBy("timestamp", "desc"),
    startAt(startingTimeStamp),
    endAt(endingTimeStamp)
  );
  const querySnapshot = await getDocs(collectionQueryRef);
  let notifications = {};
  querySnapshot.forEach((doc) => {
    if (doc.data()?.profileId === profileId) {
      notifications = {
        ...notifications,
        [doc.id]: {
          ...doc.data(),
          documentId: doc.id,
          paginationNumber
        }
      };
    }
  });
  return notifications;
}

//create new demographic
export async function createDemographic(profileName) {
  const currentUser = getCurrentUser();

  //To create patient demographic root
  const rootRef = collection(database, "patients");

  return await customAddDoc(rootRef, {
    name: profileName,
    phoneNumber: currentUser.phoneNumber
  });
}

export async function addRegData(deviceId, data) {
  await setDoc(doc(database, "devices", deviceId), data);
}

export async function addConsentAgreeToPersonalData(documentId) {
  await updateDoc(doc(database, "patients", documentId), {
    consentToPersonalData: { userAgreed: true, agreedAt: +new Date() }
  });
}

export async function careContexts(patientId) {
  const queryRef = query(
    collection(database, "patients", patientId, "careContexts"),
    where("processing", "==", false),
    where("patient.patientId", "==", patientId),
    where("clinicId", "==", lockerSetup.locker)
  );

  const querySnapshot = await getDocs(queryRef);
  let careContexts = {};
  querySnapshot.forEach((doc) => {
    careContexts = {
      ...careContexts,
      [doc.id]: {
        ...doc.data(),
        documentId: doc.id
      }
    };
  });

  return careContexts;
}
